import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { TrendingDown, TrendingUp, AlertTriangle, Building2 } from 'lucide-react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';

// Compact currency for in-bar labels: "26.4Cr" (no ₹, no extra decimals)
const fCompact = (v) => {
  if (!v || v === 0) return '';
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(0)}L`;
  return String(Math.round(n));
};

// Full currency for tooltip and narrative: "₹26.40Cr"
const fC = (v) => {
  if (!v || v === 0) return '₹0';
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.?0+$/, '')}L`;
  return `₹${n.toFixed(0)}`;
};

const DIV_COLORS = ['#2A7B88', '#378ADD', '#BA7517', '#7C3AED', '#D4726A', '#2D8B5E', '#F59E0B', '#EC4899', '#0EA5E9'];
const TREND_COLOUR = '#1B2A4A';

export default function GroupRevenueTrend() {
  const [data, setData] = useState(null);
  const [expandedBar, setExpandedBar] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);

  useEffect(() => {
    api.get('/group/revenue-history').then(r => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!data || !data.bars || data.bars.length === 0) return null;
  const hasAnyActual = data.bars.some(b => b.type === 'actual' && b.value > 0);
  if (!hasAnyActual && data.current_realised === 0) return null;

  const bars = data.bars;

  // Collect all unique divisions across history for a stable stacked series set
  const allDivs = Array.from(new Set(
    bars.flatMap(b => Object.keys(b.breakdown || {}))
  )).sort();
  const divColorMap = {};
  allDivs.forEach((d, i) => { divColorMap[d] = DIV_COLORS[i % DIV_COLORS.length]; });

  // Normalised chart rows: for each bar, one key per division + meta
  const chartData = bars.map((b) => {
    const row = { label: b.label, total: b.value, target: b.target, yoy: b.yoy_pct, type: b.type };
    allDivs.forEach(d => { row[d] = (b.breakdown || {})[d] || 0; });
    row.contracted = b.contracted || 0;
    row.contractedBreakdown = b.contracted_breakdown || {};
    // For the current FY bar, stack a translucent "targetGap" on top so the bar
    // visually reaches the annual target. Historical bars have gap = 0.
    const gap = b.type === 'current' && b.target ? Math.max(0, b.target - b.value) : 0;
    row.targetGap = gap;
    row.toGo = gap;
    // A 0-valued marker stacked on top of the full stack so Recharts gives us
    // a predictable y-coord for the total label even on historical bars where
    // targetGap is 0 (and the prior implementation occasionally failed for one).
    row.labelMarker = 0;
    row.pctAchieved = b.type === 'current' && b.target ? Math.round((b.value / b.target) * 100) : null;
    row.trendPoint = b.type === 'current' ? (b.target || b.value || 0) : (b.value || 0);
    row.yoyLabel = b.yoy_pct == null ? '' : `${b.yoy_pct < 0 ? '▼' : '▲'}${b.yoy_pct > 0 ? '+' : ''}${b.yoy_pct}%`;
    // In-bar labels use compact format (no ₹ symbol, 1 decimal max) so they fit on mobile.
    row.totalLabel = b.type === 'current' && b.target
      ? fCompact(b.target)
      : fCompact(b.value);
    return row;
  });

  const declineBars = bars.filter(b => b.yoy_pct != null && b.yoy_pct < 0);
  const worstDecline = declineBars.length > 0
    ? declineBars.reduce((w, b) => (b.yoy_pct < w.yoy_pct ? b : w), declineBars[0])
    : null;
  const hasDeclineStory = !!worstDecline;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    const entries = allDivs
      .map(d => ({ name: d, val: row[d] || 0 }))
      .filter(e => e.val > 0)
      .sort((a, b) => b.val - a.val);
    const isCurrent = row.type === 'current';
    return (
      <div className="bg-white border rounded-md px-3 py-2 shadow-sm text-[11px]" style={{ borderColor: '#E5E1DC' }}>
        {isCurrent && row.target ? (
          <>
            <p className="font-semibold" style={{ color: '#1B2A4A' }}>{label} (current FY)</p>
            <div className="mt-1 space-y-0.5">
              <p style={{ color: '#2D8B5E' }}>
                <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: '#2D8B5E' }}></span>
                <strong>Actual (realised):</strong> {fC(row.total)} <span style={{ color: '#6B7280' }}>({row.pctAchieved}% of target)</span>
              </p>
              {row.contracted > 0 && (
                <p style={{ color: '#378ADD' }}>
                  <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: '#378ADD' }}></span>
                  <strong>Contracted:</strong> {fC(row.contracted)}
                  <span style={{ color: '#6B7280' }}> ({Math.round(((row.total + row.contracted) / row.target) * 100)}% locked incl. contracts)</span>
                </p>
              )}
              <p style={{ color: '#1B2A4A' }}>
                <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: '#1B2A4A' }}></span>
                <strong>Target:</strong> {fC(row.target)}
              </p>
              <p style={{ color: '#D4726A' }}>
                <span className="inline-block w-2 h-2 rounded-sm mr-1 border border-dashed" style={{ borderColor: '#D4726A' }}></span>
                <strong>Unrealised (vs target):</strong> {fC(row.toGo)}
                {row.contracted > 0 && (
                  <span style={{ color: '#6B7280' }}>
                    {' '}· Uncovered (after contracts): {fC(Math.max(0, row.target - row.total - row.contracted))}
                  </span>
                )}
              </p>
            </div>
            {entries.length > 0 && (
              <div className="mt-1.5 pt-1.5 border-t space-y-0.5" style={{ borderColor: '#F3F4F6' }}>
                <p className="text-[10px] font-semibold" style={{ color: '#9CA3AF' }}>Actual (realised) by division</p>
                {entries.map(e => (
                  <p key={e.name} style={{ color: '#6B7280' }}>
                    <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: divColorMap[e.name] }}></span>
                    {e.name}: {fC(e.val)}
                  </p>
                ))}
              </div>
            )}
            {row.contracted > 0 && Object.keys(row.contractedBreakdown || {}).length > 0 && (
              <div className="mt-1.5 pt-1.5 border-t space-y-0.5" style={{ borderColor: '#F3F4F6' }}>
                <p className="text-[10px] font-semibold" style={{ color: '#378ADD' }}>Contracted by division</p>
                {Object.entries(row.contractedBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([name, val]) => (
                    <p key={name} style={{ color: '#6B7280' }}>
                      <span className="inline-block w-2 h-2 rounded-sm mr-1 border" style={{ borderColor: '#378ADD', backgroundColor: '#378ADD22' }}></span>
                      {name}: {fC(val)}
                    </p>
                  ))}
              </div>
            )}
          </>
        ) : (
          <>
            <p className="font-semibold" style={{ color: '#1B2A4A' }}>{label} — {fC(row.total || row.target)}</p>
            {row.yoy != null && (
              <p className="font-semibold" style={{ color: row.yoy < 0 ? '#D4726A' : '#2D8B5E' }}>YoY: {row.yoy > 0 ? '+' : ''}{row.yoy}%</p>
            )}
            <div className="mt-1 space-y-0.5">
              {entries.map(e => (
                <p key={e.name} style={{ color: '#6B7280' }}>
                  <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: divColorMap[e.name] }}></span>
                  {e.name}: {fC(e.val)}
                </p>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="group-revenue-trend">
      <div className="flex items-center gap-2">
        <Building2 size={16} style={{ color: '#2A7B88' }} />
        <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Group Revenue Journey — 5 Year Story</h3>
        {hasDeclineStory && (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>
            <AlertTriangle size={10} /> {worstDecline.label}: {worstDecline.yoy_pct}%
          </span>
        )}
      </div>

      {/* Chart */}
      <div style={{ width: '100%', height: isMobile ? 300 : 340 }}>
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
          <ComposedChart data={chartData} margin={{ top: isMobile ? 34 : 44, right: isMobile ? 6 : 20, left: isMobile ? -8 : 0, bottom: isMobile ? 4 : 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: isMobile ? 9 : 11, fill: '#1B2A4A', fontWeight: 600 }}
              interval={0}
              angle={isMobile ? -30 : 0}
              textAnchor={isMobile ? 'end' : 'middle'}
              height={isMobile ? 46 : 24}
              axisLine={{ stroke: '#E5E1DC' }}
              tickLine={false}
            />
            <YAxis tickFormatter={(v) => fCompact(v)} tick={{ fontSize: isMobile ? 9 : 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={isMobile ? 36 : 56} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F9FAFB' }} />

            {/* Stacked bars — one per division (in order) */}
            {allDivs.map((div) => (
              <Bar
                key={div}
                dataKey={div}
                stackId="divisions"
                fill={divColorMap[div]}
                barSize={isMobile ? 22 : 46}
                radius={0}
                isAnimationActive={true}
              />
            ))}

            {/* Target-gap overlay: only renders on the current FY bar.
                Translucent, dashed border, topped with the target label. */}
            <Bar
              dataKey="targetGap"
              stackId="divisions"
              fill="#D4726A"
              fillOpacity={0.12}
              stroke="#D4726A"
              strokeDasharray="4 3"
              strokeWidth={1.25}
              barSize={isMobile ? 22 : 46}
              radius={[4, 4, 0, 0]}
              isAnimationActive={true}
            >
              <LabelList dataKey="yoyLabel" position="top" offset={isMobile ? 2 : 4} content={(props) => {
                const { x, y, width, value, index } = props;
                if (!value) return null;
                const row = chartData[index];
                // Don't show YoY label on the current bar — target label serves that purpose.
                if (row?.type === 'current') return null;
                const down = row?.yoy != null && row.yoy < 0;
                return (
                  <text x={Number(x) + Number(width) / 2} y={Number(y) - (isMobile ? 2 : 4)} textAnchor="middle" fontSize={isMobile ? 8 : 10} fontWeight="600" fill={down ? '#D4726A' : '#2D8B5E'}>
                    {value}
                  </text>
                );
              }} />
            </Bar>

            {/*
              Total label overlay — a 0-value bar stacked at the very top so
              Recharts gives us the stack-top y-coord on every row, including
              historical bars where targetGap is 0. This keeps the x-alignment
              locked to the main division stack (single stackId).
            */}
            <Bar dataKey="labelMarker" stackId="divisions" fill="transparent" barSize={isMobile ? 22 : 46} isAnimationActive={false} legendType="none">
              <LabelList dataKey="totalLabel" content={(props) => {
                const { x, y, width, value, index } = props;
                if (!value) return null;
                const row = chartData[index];
                const isCurrent = row?.type === 'current';
                const display = isCurrent
                  ? (isMobile ? `🎯${value}` : `Target ${value}`)
                  : value;
                const offset = isMobile ? 14 : 18;
                return (
                  <text
                    x={Number(x) + Number(width) / 2}
                    y={Number(y) - offset}
                    textAnchor="middle"
                    fontSize={isMobile ? 9 : 11}
                    fontWeight="700"
                    fill={isCurrent ? '#D4726A' : '#1B2A4A'}
                  >
                    {display}
                  </text>
                );
              }} />
            </Bar>

            {/* Trend line */}
            <Line
              type="monotone"
              dataKey="trendPoint"
              stroke={TREND_COLOUR}
              strokeWidth={isMobile ? 1.5 : 2}
              strokeDasharray="6 4"
              dot={{ r: isMobile ? 2.5 : 4, fill: TREND_COLOUR, stroke: '#fff', strokeWidth: isMobile ? 1 : 2 }}
              activeDot={{ r: isMobile ? 3.5 : 5 }}
              isAnimationActive={true}
              legendType="none"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Division colour legend */}
      {allDivs.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
          {allDivs.map(d => (
            <div key={d} className="flex items-center gap-1 cursor-pointer" onClick={() => setExpandedBar(null)} data-testid={`legend-${d.replace(/[^a-z0-9]/gi, '-')}`}>
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: divColorMap[d] }}></span>
              <span className="text-[10px]" style={{ color: '#6B7280' }}>{d}</span>
            </div>
          ))}
        </div>
      )}

      {/* Bar click-to-expand breakdown */}
      <div className="flex flex-wrap gap-1">
        {bars.map((b, i) => (
          <button key={i}
            onClick={() => setExpandedBar(expandedBar === i ? null : i)}
            className="text-[10px] px-2 py-0.5 rounded-full border hover:bg-[#F7F5F2]"
            style={{ borderColor: '#E5E1DC', color: expandedBar === i ? '#2A7B88' : '#6B7280', fontWeight: expandedBar === i ? 600 : 400 }}>
            {b.label} breakdown
          </button>
        ))}
      </div>

      {expandedBar != null && bars[expandedBar] && (
        <div className="rounded-md p-3 space-y-1" style={{ backgroundColor: '#F7F5F2' }} data-testid="group-bar-breakdown">
          <p className="text-xs font-semibold mb-1" style={{ color: '#1B2A4A' }}>
            {bars[expandedBar].label} by division ({bars[expandedBar].type === 'current' ? 'realised YTD' : 'actual'})
          </p>
          {Object.entries(bars[expandedBar].breakdown || {}).sort(([, a], [, c]) => c - a).map(([name, val]) => {
            const total = bars[expandedBar].value || 1;
            const pct = total > 0 ? (val / total) * 100 : 0;
            return (
              <div key={name} className="flex items-center gap-2">
                <span className="text-xs flex-1 truncate" style={{ color: '#6B7280' }}>{name}</span>
                <div className="h-1.5 rounded-full flex-1 max-w-[120px]" style={{ backgroundColor: '#E5E1DC' }}>
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: divColorMap[name] || '#2A7B88' }}></div>
                </div>
                <span className="text-xs font-semibold tabular-nums" style={{ color: '#1B2A4A' }}>{fC(val)}</span>
                <span className="text-[10px] tabular-nums w-10 text-right" style={{ color: '#9CA3AF' }}>{pct.toFixed(0)}%</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Narrative */}
      <div className="pt-2 space-y-1 text-[11px] leading-relaxed" style={{ color: '#374151' }}>
        {hasDeclineStory && (
          <p>
            <TrendingDown size={10} className="inline -mt-0.5" style={{ color: '#D4726A' }} />
            <strong> Covid story visible</strong> — {worstDecline.label} dropped {Math.abs(worstDecline.yoy_pct)}% as loyalty-club contracts paused. The group has since rebuilt.
          </p>
        )}
        {data.current_fy && data.current_target > 0 && (
          <p>
            <TrendingUp size={10} className="inline -mt-0.5" style={{ color: '#2D8B5E' }} />
            <strong> {data.current_fy} Target ₹{(data.current_target/1e7).toFixed(2)}Cr</strong>
            {data.current_realised > 0 && (
              <span>
                {' '}· Actual ₹{(data.current_realised/1e7).toFixed(2)}Cr ({((data.current_realised/data.current_target)*100).toFixed(0)}%)
                {data.current_contracted > 0 && (
                  <span> · Contracted ₹{(data.current_contracted/1e7).toFixed(2)}Cr</span>
                )}
                {' '}· <span style={{ color: '#D4726A', fontWeight: 600 }}>Unrealised vs target ₹{((data.current_target-data.current_realised)/1e7).toFixed(2)}Cr</span>
                {data.current_contracted > 0 && (
                  <span style={{ color: '#9CA3AF' }}>
                    {' '}(uncovered after contracts ₹{(Math.max(0, data.current_target - data.current_realised - data.current_contracted)/1e7).toFixed(2)}Cr)
                  </span>
                )}
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
